import { FC, useEffect, useMemo, useState } from 'react'
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material'
import { AgGridReact } from 'ag-grid-react'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import type { SetFilterValuesFuncParams } from 'ag-grid-enterprise'
import dayjs from 'dayjs'
import { CloseChipIcon, InfoTooltipIcon } from 'ui/components/svg'
import { ChipTooltip } from 'ui/components/tooltip/ChipTooltip'
import { closeModal, modalPropsSelector } from 'store/modals/modalSlice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { ExclusionRulesServiceV4 } from 'services'
import { dateFormat, getIntegrationLogo } from 'lib/utils'
import { useUsersLookup } from 'hooks/useUsersLookup'
import { DEVICES_COLUMN_NAME, IDENTITY_COLUMN_NAME, RecordBatchInputSchemaItem } from 'models'
import theme from 'theme'
import { isDateRangeColumn } from 'ui/pages/cpi-details/sections/devices/utils'
import { Header, InfoBar, MetaRow, ModalRoot, RecordsTableWrapper } from './excludedRecords.styles'
import { CalendarOutlineIcon, PencilIcon, UserOutlineIcon } from './exclusionRuleIcons'

const isDateColumn = (item: RecordBatchInputSchemaItem): boolean =>
  item.dataType === 'timestamp' || isDateRangeColumn(item.filterPredicates)

const isNumberColumn = (item: RecordBatchInputSchemaItem): boolean => {
  const t = (item.dataType || '').toLowerCase()
  return t === 'integer' || t === 'long' || t === 'float' || t === 'double' || t === 'number'
}

const formatCellValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—'
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'string' && value.startsWith('[')) {
    try {
      const parsed = JSON.parse(value)
      if (Array.isArray(parsed)) return parsed.join(', ')
    } catch {
      // Fall through to String(value) below.
    }
  }
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

const humanizeFieldName = (raw: string): string =>
  raw
    .replace(/^__/, '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())

const INTERNAL_FIELDS = new Set(['SK_ID', 'IS_EXCLUDED', 'MATCHED_RULES'])

// Modal sits at z-index 10002 (see Modal.tsx); tooltips must render above that.
const MODAL_TOOLTIP_Z_INDEX = 10003

const tooltipStyle = {
  backgroundColor: `${theme.colors.tooltipBg} !important`,
  color: theme.colors.menuItemText,

  '&::before': {
    backgroundColor: `${theme.colors.tooltipBg} !important`
  },

  '& .MuiTooltip-arrow::before': {
    backgroundColor: `${theme.colors.tooltipBg} !important`
  }
}

const tooltipComponentProps = {
  tooltip: {
    sx: tooltipStyle
  }
}

const isProductNameField = (field?: string): boolean =>
  field === DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_NAME || field === IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_NAME

const isProductInstanceField = (field?: string): boolean =>
  field === DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE || field === IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE

const ExcludedRecords: FC = () => {
  const dispatch = useAppDispatch()
  const modalProps = useAppSelector(modalPropsSelector)
  const props = modalProps.props?.excludedRecordsProps
  const { getUserLabel } = useUsersLookup([props?.createdBy, props?.updatedBy].filter((id): id is string => !!id))
  const [records, setRecords] = useState<Record<string, unknown>[]>([])
  const [totalResults, setTotalResults] = useState(0)
  const [loading, setLoading] = useState(true)
  const lastUpdated = props?.lastUpdated ?? null

  useEffect(() => {
    if (!props) return
    let cancelled = false
    setLoading(true)
    ExclusionRulesServiceV4.listExcludedRecords(props.ruleId, {
      cpi_id: props.cpiSlug,
      page: 1,
      limit: 100
    })
      .then((res) => {
        if (cancelled) return
        setRecords(res.data.data || [])
        setTotalResults(res.data.pagination?.total_results || 0)
      })
      .catch((e) => {
        console.error(e)
        if (!cancelled) setRecords([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [props])

  const colDefs = useMemo<ColDef[]>(() => {
    const schema = props?.inputSchema
    const dataSourceOptions = props?.dataSourceOptions || []
    // Mirror the Off SLA Entities default view: only schema items flagged displayByDefault,
    // with the bookkeeping fields (SK_ID, IS_EXCLUDED, MATCHED_RULES) stripped even when a
    // manifest marks them default-visible.
    const visibleFromSchema =
      schema && schema.length > 0
        ? schema.filter(
            (s) => !!s.displayName && !!s.filterPredicates && s.displayByDefault && !INTERNAL_FIELDS.has(s.name)
          )
        : []

    const renderTextCell = (text: string) => (
      <Tooltip
        title={text}
        placement="right"
        arrow
        componentsProps={tooltipComponentProps}
        PopperProps={{ style: { zIndex: MODAL_TOOLTIP_Z_INDEX } }}
      >
        <div
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {text}
        </div>
      </Tooltip>
    )

    const renderProductNameCell = (params: ICellRendererParams) => {
      const productName =
        params.data?.[DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_NAME] ||
        params.data?.[IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_NAME]
      if (!productName) return <div>—</div>
      return (
        <div className="source-wrapper">
          <ChipTooltip
            placement="bottom-start"
            PopperProps={{
              style: { zIndex: MODAL_TOOLTIP_Z_INDEX },
              sx: {
                marginTop: `-${theme.spacing.half}px !important`
              }
            }}
            title={
              <Box className="tooltip-wrapper-permissions one-line">
                <Typography className="tooltip-text">{productName}</Typography>
              </Box>
            }
          >
            <img src={getIntegrationLogo(productName)} width="24px" height="24px" alt="" />
          </ChipTooltip>
        </div>
      )
    }

    const renderProductInstanceCell = (params: ICellRendererParams) => {
      const value =
        params.data?.[DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE] ||
        params.data?.[IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE]
      const match = dataSourceOptions.find((c) => c.instance_name === value)
      return renderTextCell(match?.name ? String(match.name) : formatCellValue(value))
    }

    // Build set-filter values from in-memory records — same pattern OV/ER use.
    const setFilterValuesFor = (field: string) => (params: SetFilterValuesFuncParams) => {
      const collected = new Set<string>()
      params.api.forEachNode((node) => {
        const data: any = node.data
        if (!data) return
        const v = data[field]
        if (v != null && v !== '') collected.add(String(v))
      })
      params.success(Array.from(collected).sort())
    }

    const setFilterParams = (field: string) => ({
      includeBlankValues: true,
      suppressMiniFilter: false,
      defaultToNothingSelected: true,
      cellHeight: 38,
      buttons: ['apply', 'reset'] as ('apply' | 'reset')[],
      closeOnApply: true,
      values: setFilterValuesFor(field)
    })

    // Fallback: if the manifest has no displayable columns, derive them from the first record's keys
    // so the user still sees something instead of a blank modal.
    if (visibleFromSchema.length === 0 && records.length > 0) {
      const first = records[0]
      return Object.keys(first)
        .filter((k) => !INTERNAL_FIELDS.has(k))
        .map<ColDef>((key) => ({
          field: key,
          headerName: humanizeFieldName(key),
          sortable: true,
          flex: 1,
          minWidth: 160,
          suppressHeaderMenuButton: true,
          filter: 'agSetColumnFilter',
          filterParams: setFilterParams(key),
          cellRenderer: (params: ICellRendererParams) => {
            if (isProductNameField(key)) return renderProductNameCell(params)
            if (isProductInstanceField(key)) return renderProductInstanceCell(params)
            const v = params.value
            if (key === '__time' && v) {
              return renderTextCell(dayjs(v as string).format(dateFormat.customLabelHour))
            }
            return renderTextCell(formatCellValue(v))
          }
        }))
    }

    return visibleFromSchema.map<ColDef>((item) => {
      const base: ColDef = {
        field: item.name,
        headerName: item.displayName,
        sortable: true,
        flex: 1,
        minWidth: 160,
        suppressHeaderMenuButton: true,
        cellRenderer: (params: ICellRendererParams) => {
          if (isProductNameField(item.name)) return renderProductNameCell(params)
          if (isProductInstanceField(item.name)) return renderProductInstanceCell(params)
          const v = params.value
          if (isDateColumn(item) && v) {
            return renderTextCell(dayjs(v as string).format(dateFormat.customLabelHour))
          }
          return renderTextCell(formatCellValue(v))
        }
      }

      if (isDateColumn(item)) {
        return {
          ...base,
          filter: 'agDateColumnFilter',
          filterParams: {
            browserDatePicker: true,
            maxNumConditions: 1,
            inRangeFloatingFilterDateFormat: 'Do MMM YYYY',
            buttons: ['reset'],
            comparator: (filterDate: Date, cellValue: unknown) => {
              if (cellValue == null || cellValue === '') return -1
              const cellDate = dayjs(cellValue as string)
                .startOf('day')
                .toDate()
              const filterDay = dayjs(filterDate).startOf('day').toDate()
              const diff = cellDate.getTime() - filterDay.getTime()
              if (diff < 0) return -1
              if (diff > 0) return 1
              return 0
            }
          }
        }
      }

      if (isNumberColumn(item)) {
        return {
          ...base,
          filter: 'agNumberColumnFilter'
        }
      }

      // Strings and booleans both use set filter for consistency with OV/ER.
      // The product-instance set filter maps stored instance_name → display name so the
      // dropdown shows the same human-readable values as the cells render.
      if (isProductInstanceField(item.name)) {
        return {
          ...base,
          filter: 'agSetColumnFilter',
          filterParams: {
            ...setFilterParams(item.name),
            values: (params: SetFilterValuesFuncParams) => {
              const collected = new Set<string>()
              params.api.forEachNode((node) => {
                const data: any = node.data
                if (!data) return
                const raw = data[item.name]
                if (raw == null || raw === '') return
                const match = dataSourceOptions.find((c) => c.instance_name === raw)
                collected.add(match?.name ? String(match.name) : String(raw))
              })
              params.success(Array.from(collected).sort())
            }
          }
        }
      }

      return {
        ...base,
        filter: 'agSetColumnFilter',
        filterParams: setFilterParams(item.name)
      }
    })
  }, [props, records])

  const defaultColDef = useMemo<ColDef>(
    () => ({
      flex: 1,
      minWidth: 120,
      resizable: true,
      suppressHeaderMenuButton: true
    }),
    []
  )

  if (!props) return null

  const { onEdit, lastEditedAt, updatedBy } = props
  const editedHint = updatedBy ? `Last edited by ${getUserLabel(updatedBy)}` : ''

  return (
    <ModalRoot>
      <Header>
        <Typography className="title">{props.ruleName}</Typography>
        <Box className="header-actions">
          {onEdit && (
            <Tooltip
              title="Edit Exclusion Rule"
              placement="bottom"
              componentsProps={tooltipComponentProps}
              PopperProps={{ style: { zIndex: MODAL_TOOLTIP_Z_INDEX } }}
            >
              <IconButton className="edit-button" aria-label="Edit Exclusion Rule" onClick={onEdit}>
                <PencilIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton aria-label="Close" onClick={() => dispatch(closeModal())}>
            <CloseChipIcon />
          </IconButton>
        </Box>
      </Header>

      <MetaRow>
        <Box className="meta-tile">
          <span className="meta-icon">
            <CalendarOutlineIcon />
          </span>
          <Box>
            <Typography className="meta-label">Creation Date</Typography>
            <Typography className="meta-value">{dayjs(props.createdAt).format(dateFormat.auditLogDefault)}</Typography>
          </Box>
        </Box>
        <Box className="meta-tile">
          <span className="meta-icon">
            <UserOutlineIcon />
          </span>
          <Box>
            <Typography className="meta-label">Created by</Typography>
            <Typography className="meta-value">{getUserLabel(props.createdBy)}</Typography>
          </Box>
        </Box>
        <Box className="meta-tile">
          <span className="meta-icon">
            <PencilIcon />
          </span>
          <Box>
            <Typography className="meta-label">
              Last Edited
              {editedHint && (
                <Tooltip
                  title={editedHint}
                  placement="top"
                  componentsProps={tooltipComponentProps}
                  PopperProps={{ style: { zIndex: MODAL_TOOLTIP_Z_INDEX } }}
                >
                  <span className="meta-hint" aria-label={editedHint}>
                    <InfoTooltipIcon />
                  </span>
                </Tooltip>
              )}
            </Typography>
            <Typography className="meta-value">
              {lastEditedAt ? dayjs(lastEditedAt).format(dateFormat.auditLogDefault) : '—'}
            </Typography>
          </Box>
        </Box>
      </MetaRow>

      <Box className="reason">
        <Typography className="reason-label">Reason for Exclusion</Typography>
        <Typography className="reason-value">{props.reason || '—'}</Typography>
      </Box>

      <InfoBar>
        <Typography className="records-count">
          {loading ? '…' : totalResults.toLocaleString('en-US')} Record(s) Excluded
        </Typography>
        {lastUpdated && (
          <Typography>
            Last Update: <span>{dayjs(lastUpdated).format('ddd, MMM DD, YYYY')}</span>
          </Typography>
        )}
      </InfoBar>

      <RecordsTableWrapper>
        {loading ? (
          <div className="loading">
            <CircularProgress size="24px" sx={{ color: theme.colors.cta }} />
          </div>
        ) : records.length === 0 ? (
          <div className="empty">No excluded records yet.</div>
        ) : colDefs.length === 0 ? (
          <div className="empty">No displayable columns defined in manifest.</div>
        ) : (
          <div className="grid-host">
            <AgGridReact
              rowData={records}
              columnDefs={colDefs}
              defaultColDef={defaultColDef}
              rowHeight={64}
              rowStyle={{ maxHeight: '60px' }}
              headerHeight={49}
              animateRows={false}
              suppressContextMenu
            />
          </div>
        )}
      </RecordsTableWrapper>
    </ModalRoot>
  )
}

export default ExcludedRecords
